// CTV-610: landing-hero background-video builder. Publisher uploads 1-6 clips,
// then arranges them on a fixed 24s timeline (reorder, set each clip's length,
// pick which part plays). Saving the channel stitches them (with the baked
// "clean first 3s, then blur/dim/vignette" treatment) into the TV-hero video.
//
// This component owns the upload + composition; the render is kicked by
// PublisherForm's submit once the station has a slug.

import React from 'react';
import { Upload, Loader2, Rocket, CheckCircle2, Plus, Clapperboard } from 'lucide-react';
import {
  uploadBackgroundClip,
  buildStationBackgroundVideo,
  fetchIntakeFootage,
  ACCEPTED_CLIP_TYPES,
  MAX_BACKGROUND_CLIPS,
  MAX_CLIP_BYTES,
  type IntakeFootageClip,
} from './backgroundVideoApi';
import ClipTimeline from './ClipTimelineEditor';
import TimelinePreview from './TimelinePreview';
import { createClipPlayer, type ClipPlayer } from './clipPlayer';
import { defaultLength, totalLength, toRenderClips, type TimelineClip } from './clipTimeline';
import { extractThumbnails } from './clipThumbnails';
import { clipLeafName } from './publisherIntakeToConfig';

export type { TimelineClip };

// Read a video's duration from a URL before it lands on the timeline, so we
// can seed a sensible starting length + cap trims.
function probeUrl(url: string): Promise<{ url: string; durationSec: number }> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => resolve({ url, durationSec: v.duration || 0 });
    v.onerror = () => resolve({ url, durationSec: 0 });
    v.src = url;
  });
}

// Read a file's duration (and keep the object URL for preview).
function probeClip(file: File): Promise<{ url: string; durationSec: number }> {
  return probeUrl(URL.createObjectURL(file));
}

interface Props {
  /** The station's already-rendered video, shown when nothing is composed yet. */
  currentVideoUrl?: string | null;
  /** Station slug — present in edit mode; enables the "Save & publish" button. */
  slug?: string | null;
  clips: TimelineClip[];
  onChange: (clips: TimelineClip[]) => void;
}

const BackgroundVideoBuilder: React.FC<Props> = ({
  currentVideoUrl,
  slug,
  clips,
  onChange,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [publishError, setPublishError] = React.useState<string | null>(null);
  const [publishedUrl, setPublishedUrl] = React.useState<string | null>(null);
  const [intakeFootage, setIntakeFootage] = React.useState<IntakeFootageClip[]>([]);
  const [addingIntakePath, setAddingIntakePath] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // CTV-745: in edit mode, offer the raw footage the publisher submitted with
  // their onboarding form so the landing video can be re-cut after finalize.
  // Best-effort: a fetch failure just leaves the tray hidden.
  React.useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetchIntakeFootage(slug)
      .then((footage) => {
        if (!cancelled) setIntakeFootage(footage);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // onChange batches appends during the async upload loop; read the latest list
  // from a ref so successive appends don't clobber each other.
  const clipsRef = React.useRef(clips);
  clipsRef.current = clips;

  // Shared playback controller: the timeline scrubber and the preview player
  // both drive it, keeping the playhead and the video in sync.
  const playerRef = React.useRef<ClipPlayer>();
  if (!playerRef.current) playerRef.current = createClipPlayer();
  const player = playerRef.current;
  player.setClips(clips);

  const atCapacity = clips.length >= MAX_BACKGROUND_CLIPS;

  const upload = async (files: File[]) => {
    setError(null);
    const room = MAX_BACKGROUND_CLIPS - clips.length;
    if (room <= 0) {
      setError(`Up to ${MAX_BACKGROUND_CLIPS} clips.`);
      return;
    }
    const batch = files.slice(0, room);
    for (const file of batch) {
      if (!ACCEPTED_CLIP_TYPES.includes(file.type)) {
        setError('Clips must be MP4, MOV, or WebM.');
        return;
      }
      if (file.size > MAX_CLIP_BYTES) {
        setError(`Each clip must be under ${MAX_CLIP_BYTES / 1024 / 1024} MB.`);
        return;
      }
    }
    setUploading(true);
    try {
      for (const file of batch) {
        const { url, durationSec } = await probeClip(file);
        const storagePath = await uploadBackgroundClip(file);
        const current = clipsRef.current;
        const id = crypto.randomUUID();
        onChange([
          ...current,
          {
            id,
            storagePath,
            name: file.name,
            previewUrl: url,
            durationSec,
            inSec: 0,
            lengthSec: defaultLength(durationSec, totalLength(current)),
          },
        ]);
        // Fill the filmstrip in the background; patch the clip (by id) once ready.
        void extractThumbnails(url).then((thumbnails) => {
          if (thumbnails.length) {
            onChange(clipsRef.current.map((c) => (c.id === id ? { ...c, thumbnails } : c)));
          }
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Publisher footage not already placed on the timeline.
  const availableFootage = intakeFootage.filter(
    (f) => !clips.some((c) => c.storagePath === f.storagePath),
  );

  // Place an intake clip on the timeline. No upload: the object already lives
  // in the clips bucket, so this only probes its duration for a starting
  // length and previews it through the signed URL.
  const addIntakeClip = async (footage: IntakeFootageClip) => {
    if (atCapacity || addingIntakePath) return;
    setError(null);
    setAddingIntakePath(footage.storagePath);
    try {
      const { durationSec } = await probeUrl(footage.url);
      const current = clipsRef.current;
      const id = crypto.randomUUID();
      onChange([
        ...current,
        {
          id,
          storagePath: footage.storagePath,
          name: clipLeafName(footage.storagePath) || 'Onboarding clip',
          previewUrl: footage.url,
          durationSec,
          inSec: 0,
          lengthSec: defaultLength(durationSec, totalLength(current)),
        },
      ]);
      void extractThumbnails(footage.url).then((thumbnails) => {
        if (thumbnails.length) {
          onChange(clipsRef.current.map((c) => (c.id === id ? { ...c, thumbnails } : c)));
        }
      });
    } finally {
      setAddingIntakePath(null);
    }
  };

  // Render + attach the composed video now (edit mode, slug present). The build
  // bakes the treatment server-side; on success the station points at the new
  // mp4 and we surface it inline.
  const publish = async () => {
    if (!slug || !clips.length) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const url = await buildStationBackgroundVideo(slug, toRenderClips(clips));
      setPublishedUrl(url);
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const liveVideoUrl = publishedUrl ?? currentVideoUrl;

  return (
    <div className="space-y-3">
      {liveVideoUrl && clips.length === 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <video
            src={liveVideoUrl}
            muted
            loop
            playsInline
            className="h-16 w-28 rounded-md object-cover bg-black shrink-0"
          />
          <p className="text-[12px] text-slate-600 leading-relaxed">
            This channel has a background video. Upload clips below to build a replacement.
          </p>
        </div>
      )}

      {availableFootage.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Clapperboard className="w-3.5 h-3.5 text-slate-400" />
            <p className="text-[11px] text-slate-500 uppercase tracking-[0.08em] font-medium">
              Footage from the publisher's onboarding submission
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {availableFootage.map((f) => (
              <div
                key={f.storagePath}
                className="flex flex-col gap-1.5 rounded-lg border border-slate-200 p-2 w-32"
              >
                <video
                  src={f.url}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-16 w-28 rounded-md object-cover bg-black"
                />
                <span
                  className="text-[11px] text-slate-600 truncate"
                  title={clipLeafName(f.storagePath)}
                >
                  {clipLeafName(f.storagePath) || 'Onboarding clip'}
                </span>
                <button
                  type="button"
                  onClick={() => void addIntakeClip(f)}
                  disabled={atCapacity || addingIntakePath !== null}
                  className="inline-flex items-center justify-center gap-1 rounded-full border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-500 disabled:opacity-50 transition-colors"
                >
                  {addingIntakePath === f.storagePath ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                  Add to timeline
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const dropped = Array.from(e.dataTransfer.files);
          if (dropped.length) void upload(dropped);
        }}
        onClick={() => !uploading && !atCapacity && inputRef.current?.click()}
        className={
          'rounded-xl border-2 border-dashed p-5 text-center transition-colors ' +
          (atCapacity
            ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-70'
            : 'cursor-pointer ' +
              (isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'))
        }
        aria-disabled={uploading || atCapacity}
      >
        <div className="flex items-center justify-center gap-2 text-[13px] text-slate-600">
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          ) : atCapacity ? (
            <>Maximum {MAX_BACKGROUND_CLIPS} clips added.</>
          ) : (
            <>
              <Upload className="w-4 h-4 text-slate-400" />
              Drop clips, or{' '}
              <span className="font-semibold text-slate-900 underline underline-offset-2 decoration-1">
                browse
              </span>
            </>
          )}
        </div>
        <p className="mt-1 text-[11px] text-slate-400 uppercase tracking-[0.08em] font-medium">
          MP4 · MOV · WebM — up to {MAX_BACKGROUND_CLIPS} clips, 150 MB each
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_CLIP_TYPES.join(',')}
          multiple
          onChange={(e) => {
            const selected = Array.from(e.target.files ?? []);
            if (selected.length) void upload(selected);
            e.target.value = '';
          }}
          className="hidden"
        />
      </div>

      {error && <p className="text-xs text-red-500 px-1">{error}</p>}

      {clips.length > 0 && (
        <>
          <ClipTimeline clips={clips} onChange={onChange} player={player} />
          <TimelinePreview player={player} />

          {publishedUrl && (
            <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
              <p className="text-[13px] text-emerald-800 leading-relaxed">
                Background video published, it's now live on the channel's landing page. Re-publish
                any time after editing.
              </p>
            </div>
          )}
          {publishError && (
            <p className="text-xs text-red-500 px-1">{publishError}</p>
          )}

          {slug ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={publish}
                disabled={publishing}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {publishing ? 'Rendering & publishing…' : 'Save & publish video'}
              </button>
            </div>
          ) : (
            <p className="text-[12px] text-slate-500 text-right">
              Create the channel first, then its background video renders on save.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default BackgroundVideoBuilder;
